import { useEffect, useState } from 'react';

export const APPEAL_MIN_CHARS = 30;
export const APPEAL_MAX_CHARS = 1000;

type AppealFormState = {
  description: string;
  trimmed: string;
  touched: boolean;
  tooShort: boolean;
  error: string | undefined;
  setText: (v: string) => void;
  // Replace the current textarea content with a preset. Marks the form as
  // touched so subsequent edits still trigger validation feedback the same
  // way they would after typing manually.
  applyTemplate: (text: string) => void;
};

function resolveError(
  touched: boolean,
  tooShort: boolean,
  server: string | undefined
): string | undefined {
  if (server) return server;
  if (touched && tooShort) return `Mínimo ${APPEAL_MIN_CHARS} caracteres`;
  return undefined;
}

function useServerError(initial: string | undefined) {
  const [server, setServer] = useState(initial);
  useEffect(() => setServer(initial), [initial]);
  return [server, setServer] as const;
}

type HandlerDeps = {
  setDescription: (v: string) => void;
  setTouched: (v: boolean) => void;
  server: string | undefined;
  setServer: (v: string | undefined) => void;
  touched: boolean;
};

function makeHandlers(d: HandlerDeps) {
  const clearErr = (): void => {
    if (d.server) d.setServer(undefined);
  };
  const setText = (v: string): void => {
    d.setDescription(v);
    if (!d.touched) d.setTouched(true);
    clearErr();
  };
  const applyTemplate = (text: string): void => {
    d.setDescription(text.slice(0, APPEAL_MAX_CHARS));
    d.setTouched(true);
    clearErr();
  };
  return { setText, applyTemplate };
}

// Local state for the appeal textarea. `initialServerError` is mirrored into
// local state so we can clear it the moment the user starts editing again —
// otherwise a stale server-side message would linger while they're fixing it.
export function useAppealForm(
  initialServerError: string | undefined
): AppealFormState {
  const [description, setDescription] = useState('');
  const [touched, setTouched] = useState(false);
  const [server, setServer] = useServerError(initialServerError);
  const trimmed = description.trim();
  const tooShort = trimmed.length < APPEAL_MIN_CHARS;
  const error = resolveError(touched, tooShort, server);
  const h = makeHandlers({
    setDescription,
    setTouched,
    server,
    setServer,
    touched,
  });
  return { description, trimmed, touched, tooShort, error, ...h };
}
